import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Phone, MessageCircle, Shield, Award, Users, TrendingDown, Clock, CheckCircle2 } from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { WhatsAppButton } from '../components/WhatsAppButton';
import { Button } from '../components/ui/button';
import { businessInfo } from '../mockData';

export const VersicherungPage = () => {
  const openWhatsAppVersicherung = () => {
    const message = encodeURIComponent('Hallo, ich interessiere mich für eine Kfz-Versicherungsberatung.');
    window.open(`https://wa.me/${businessInfo.whatsappNumber}?text=${message}`, '_blank');
  };

  return (
    <>
      <Helmet>
        <title>HDI Kfz-Versicherung Velbert | Hauptvertretung Mazlum Aksu - Bis zu 20% Neukundenrabatt</title>
        <meta name="description" content="HDI Hauptvertretung Mazlum Aksu in Velbert-Langenberg. Persönliche Kfz-Versicherungsberatung, faire Tarife und bis zu 20% Neukundenrabatt. Jetzt Angebot einholen!" />
        <link rel="canonical" href="https://mazulassungsdienst.de/versicherung" />
        <meta property="og:title" content="HDI Kfz-Versicherung - Hauptvertretung Mazlum Aksu" />
        <meta property="og:description" content="Persönliche Versicherungsberatung und faire HDI-Tarife in Velbert-Langenberg." />
      </Helmet>

      <div className="min-h-screen bg-white">
        <Header />
        
        <main>
          {/* Hero Section */}
          <section className="relative pt-32 pb-20 bg-gradient-to-b from-blue-50 to-white">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="grid lg:grid-cols-2 gap-12 items-center">
                <div>
                  <div className="inline-flex items-center space-x-2 bg-blue-100 text-blue-800 px-4 py-2 rounded-full mb-6">
                    <Shield className="w-4 h-4" />
                    <span className="text-sm font-semibold">Offizielle HDI Hauptvertretung</span>
                  </div>
                  
                  <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                    Ihre Kfz-Versicherung bei HDI Velbert
                  </h1>
                  
                  <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                    Als HDI Hauptvertretung Mazlum Aksu in Velbert-Langenberg bieten wir Ihnen persönliche Beratung, faire Tarife und individuellen Service für Ihre Kfz-Versicherung.
                  </p>
                  
                  <div className="bg-green-50 border-2 border-green-200 rounded-xl p-6 mb-8">
                    <p className="text-lg font-bold text-green-900 mb-2">
                      🎁 Neukundenrabatt bis zu 20%
                    </p>
                    <p className="text-gray-700">
                      Profitieren Sie von attraktiven Konditionen beim Wechsel zu HDI.
                    </p>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-4">
                    <Button 
                      size="lg" 
                      onClick={openWhatsAppVersicherung}
                      className="bg-blue-700 hover:bg-blue-800 text-white px-8 py-6 text-lg"
                    >
                      <MessageCircle className="w-5 h-5 mr-2" />
                      Jetzt Beratung anfragen
                    </Button>
                    <Button 
                      size="lg" 
                      variant="outline"
                      onClick={() => window.location.href = `tel:${businessInfo.phone}`}
                      className="border-blue-700 text-blue-700 hover:bg-blue-50 px-8 py-6 text-lg"
                    >
                      <Phone className="w-5 h-5 mr-2" />
                      {businessInfo.phoneDisplay}
                    </Button>
                  </div>
                </div>

                <div className="relative">
                  <div className="bg-white rounded-2xl shadow-2xl p-8">
                    <div className="flex items-center justify-center mb-6">
                      <img 
                        src="/hdi_logo_final.webp" 
                        alt="HDI Versicherung Logo" 
                        className="h-16 w-auto"
                      />
                    </div>
                    <div className="text-center">
                      <h3 className="text-2xl font-bold text-gray-900 mb-2">
                        HDI Hauptvertretung
                      </h3>
                      <p className="text-xl text-blue-700 font-semibold mb-4">
                        Mazlum Aksu
                      </p>
                      <p className="text-gray-600 mb-6">
                        Velbert-Langenberg
                      </p>
                      <div className="border-t pt-6">
                        <p className="text-sm text-gray-500 mb-2">Ihr Ansprechpartner für:</p>
                        <ul className="text-left space-y-2 text-gray-700">
                          <li className="flex items-start">
                            <CheckCircle2 className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                            <span>Kfz-Haftpflichtversicherung</span>
                          </li>
                          <li className="flex items-start">
                            <CheckCircle2 className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                            <span>Vollkasko & Teilkasko</span>
                          </li>
                          <li className="flex items-start">
                            <CheckCircle2 className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                            <span>eVB-Nummern</span>
                          </li>
                          <li className="flex items-start">
                            <CheckCircle2 className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                            <span>Schadenabwicklung</span>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Vorteile */}
          <section className="py-20 bg-white">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-12">
                <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
                  Warum HDI Hauptvertretung Mazlum Aksu?
                </h2>
                <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                  Persönliche Beratung, faire Konditionen und erstklassiger Service – das zeichnet uns aus.
                </p>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                <div className="bg-gray-50 p-8 rounded-xl">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                    <Users className="w-6 h-6 text-blue-700" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    Persönlicher Ansprechpartner
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    Kein Callcenter, keine Warteschleifen. Bei uns erreichen Sie direkt Ihren persönlichen Berater.
                  </p>
                </div>

                <div className="bg-gray-50 p-8 rounded-xl">
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                    <TrendingDown className="w-6 h-6 text-green-700" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    Faire Tarife
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    Profitieren Sie von attraktiven HDI-Tarifen und bis zu 20% Neukundenrabatt bei uns.
                  </p>
                </div>

                <div className="bg-gray-50 p-8 rounded-xl">
                  <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center mb-4">
                    <Award className="w-6 h-6 text-yellow-700" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    HDI-Qualität
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    Vertrauen Sie auf einen der führenden Versicherer Deutschlands mit über 120 Jahren Erfahrung.
                  </p>
                </div>

                <div className="bg-gray-50 p-8 rounded-xl">
                  <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                    <Clock className="w-6 h-6 text-purple-700" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    Schnelle Bearbeitung
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    Benötigen Sie eine eVB-Nummer? Nach Beratung und Vertragsabschluss erhalten Sie diese zeitnah.
                  </p>
                </div>

                <div className="bg-gray-50 p-8 rounded-xl">
                  <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mb-4">
                    <Shield className="w-6 h-6 text-red-700" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    Umfassender Schutz
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    Von Haftpflicht über Teilkasko bis Vollkasko – wir finden den passenden Schutz für Sie.
                  </p>
                </div>

                <div className="bg-gray-50 p-8 rounded-xl">
                  <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center mb-4">
                    <CheckCircle2 className="w-6 h-6 text-indigo-700" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    Alles aus einer Hand
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    Kombinieren Sie Versicherung und Zulassung bei M.A. Zulassungsdienst – maximale Zeitersparnis.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Leistungen */}
          <section className="py-20 bg-gray-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-12">
                <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
                  Unsere Versicherungsleistungen
                </h2>
              </div>

              <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
                <div className="bg-white p-8 rounded-xl shadow-sm">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">
                    Kfz-Haftpflichtversicherung
                  </h3>
                  <p className="text-gray-600 mb-4">
                    Die gesetzlich vorgeschriebene Grundabsicherung für Ihr Fahrzeug. Deckt Schäden ab, die Sie anderen Verkehrsteilnehmern zufügen.
                  </p>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start">
                      <CheckCircle2 className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span>Deckungssummen bis 100 Mio. €</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle2 className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span>Mallorca-Police inklusive</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle2 className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span>Rabattschutz möglich</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-white p-8 rounded-xl shadow-sm">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">
                    Teilkasko-Versicherung
                  </h3>
                  <p className="text-gray-600 mb-4">
                    Erweiterte Absicherung für Schäden an Ihrem eigenen Fahrzeug durch äußere Einflüsse.
                  </p>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start">
                      <CheckCircle2 className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span>Diebstahl & Glasbruch</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle2 className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span>Brand & Explosion</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle2 className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span>Sturm, Hagel & Unwetter</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-white p-8 rounded-xl shadow-sm">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">
                    Vollkasko-Versicherung
                  </h3>
                  <p className="text-gray-600 mb-4">
                    Umfassendster Schutz für Ihr Fahrzeug – auch bei selbstverschuldeten Unfällen und Vandalismus.
                  </p>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start">
                      <CheckCircle2 className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span>Alle Teilkasko-Leistungen</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle2 className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span>Selbstverschuldete Unfälle</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle2 className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span>Vandalismus & Fahrerflucht</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-white p-8 rounded-xl shadow-sm">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">
                    eVB-Nummern & Zusatzleistungen
                  </h3>
                  <p className="text-gray-600 mb-4">
                    Alles für Ihre Fahrzeugzulassung und darüber hinaus.
                  </p>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start">
                      <CheckCircle2 className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span>Elektronische Versicherungsbestätigung</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle2 className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span>Schutzbrief & Auslandsschutz</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle2 className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span>Verkehrsrechtsschutz</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* Verbindung zu M.A. Zulassungsdienst */}
          <section className="py-20 bg-blue-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="max-w-4xl mx-auto">
                <div className="text-center mb-8">
                  <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
                    Versicherung + Zulassung aus einer Hand
                  </h2>
                  <p className="text-xl text-gray-600">
                    Maximale Zeitersparnis durch die Kombination beider Services
                  </p>
                </div>

                <div className="bg-white rounded-2xl p-8 shadow-lg">
                  <div className="grid md:grid-cols-2 gap-8">
                    <div>
                      <h3 className="text-xl font-bold text-blue-700 mb-4">
                        HDI Hauptvertretung Mazlum Aksu
                      </h3>
                      <ul className="space-y-3 text-gray-700">
                        <li className="flex items-start">
                          <CheckCircle2 className="w-5 h-5 text-blue-600 mr-2 flex-shrink-0 mt-0.5" />
                          <span>Kfz-Versicherungsberatung</span>
                        </li>
                        <li className="flex items-start">
                          <CheckCircle2 className="w-5 h-5 text-blue-600 mr-2 flex-shrink-0 mt-0.5" />
                          <span>eVB-Nummern-Ausstellung</span>
                        </li>
                        <li className="flex items-start">
                          <CheckCircle2 className="w-5 h-5 text-blue-600 mr-2 flex-shrink-0 mt-0.5" />
                          <span>Tarifvergleich & Optimierung</span>
                        </li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-bold text-green-700 mb-4">
                        M.A. Zulassungsdienst
                      </h3>
                      <ul className="space-y-3 text-gray-700">
                        <li className="flex items-start">
                          <CheckCircle2 className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                          <span>Schnellzulassung in 20 Minuten</span>
                        </li>
                        <li className="flex items-start">
                          <CheckCircle2 className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                          <span>An-, Um- und Abmeldungen</span>
                        </li>
                        <li className="flex items-start">
                          <CheckCircle2 className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                          <span>Wunschkennzeichen-Service</span>
                        </li>
                      </ul>
                    </div>
                  </div>

                  <div className="mt-8 pt-8 border-t border-gray-200">
                    <p className="text-sm text-gray-600 leading-relaxed">
                      <strong className="text-gray-900">Rechtlicher Hinweis:</strong> Die HDI Hauptvertretung Mazlum Aksu und M.A. Zulassungsdienst werden vom selben Inhaber betrieben, sind jedoch rechtlich eigenständige Geschäftsbereiche. Die Versicherungsvermittlung erfolgt ausschließlich über die HDI Hauptvertretung. M.A. Zulassungsdienst ist ein privater Zulassungsservice und keine Versicherungsagentur.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* CTA */}
          <section className="py-20 bg-blue-700 text-white">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
              <h2 className="text-3xl sm:text-4xl font-bold mb-6">
                Jetzt persönliche Beratung vereinbaren
              </h2>
              <p className="text-xl mb-8 text-blue-100">
                Kontaktieren Sie uns für ein individuelles Angebot und profitieren Sie von bis zu 20% Neukundenrabatt
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
                <Button 
                  size="lg" 
                  onClick={openWhatsAppVersicherung}
                  className="bg-white text-blue-700 hover:bg-gray-100 px-8 py-6 text-lg"
                >
                  <MessageCircle className="w-5 h-5 mr-2" />
                  WhatsApp-Beratung
                </Button>
                <Button 
                  size="lg" 
                  variant="outline"
                  onClick={() => window.location.href = `tel:${businessInfo.phone}`}
                  className="border-white text-white hover:bg-blue-800 px-8 py-6 text-lg"
                >
                  <Phone className="w-5 h-5 mr-2" />
                  Jetzt anrufen
                </Button>
              </div>

              <div className="bg-blue-800 rounded-xl p-6">
                <p className="text-sm text-blue-200">
                  <strong className="text-white">Öffnungszeiten:</strong> Mo-Do 10:00-17:30 Uhr | Fr 10:00-15:00 Uhr<br />
                  Kamper Str. 8, 42555 Velbert-Langenberg
                </p>
              </div>
            </div>
          </section>
        </main>

        <Footer />
        <WhatsAppButton />
      </div>
    </>
  );
};
